import { useState, useEffect } from 'react';
import { Modal, Input, Button, Card, Tag, Empty, Spin, message } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import authenticationApi from 'apis/authentication';
import i18n from 'i18n';
import LOCALS from 'commons/locals';
import formatTime from 'utils/formatTime';
import { getAuthenticationDetailVerdictStyle } from './form-items/authentication-detail-modal';
import {
  getAuthenticationDisplaySummary,
  getAuthenticationDisplayVerdictText,
} from './form-items/authentication-display';

export type TaskItem = {
  id: number;
  authenticationBagId: number;
  authenticationId: string;
  certificateNo?: string;
  bagId: string;
  bagInfo: Record<string, unknown>;
  status: string;
  createdAt: string;
  data: Record<string, unknown>;
  result: {
    verdict?: string;
    authenticityScore?: number;
    reviewStatus?: 'pending' | 'genuine' | 'fake';
    detectionPoints?: Array<{
      labelId?: number | null;
      score?: number | null;
    }>;
  } | null;
};

type BindAuthenticationResultModalProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (item: TaskItem) => void | Promise<void>;
};

const BindAuthenticationResultModal = ({
  open,
  onClose,
  onSelect,
}: BindAuthenticationResultModalProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [bindingId, setBindingId] = useState<number | null>(null);
  const [taskList, setTaskList] = useState<TaskItem[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchTasks = async (certificateNo?: string) => {
    setSearching(true);
    setHasSearched(true);
    try {
      const res = await authenticationApi.getAuthenticationTasks({
        pageNum: 1,
        pageSize: 10,
        ...(certificateNo && { certificateNo }),
      });
      setTaskList(res?.list || []);
    } catch {
      setTaskList([]);
      message.error(String(i18n.t(LOCALS.ai_identify_result_fetch_failed)));
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSearchValue('');
      setBindingId(null);
      fetchTasks();
    }
  }, [open]);

  const handleSearch = async (value: string) => {
    const trimmed = value.trim();
    fetchTasks(trimmed || undefined);
  };

  const handleSelect = async (item: TaskItem) => {
    setBindingId(item.authenticationBagId);
    try {
      await onSelect(item);
      message.success(String(i18n.t(LOCALS.successful_operation)));
      handleClose();
    } catch {
      message.error(String(i18n.t(LOCALS.bind_authentication_failed)));
    } finally {
      setBindingId(null);
    }
  };

  const handleClose = () => {
    setSearchValue('');
    setTaskList([]);
    setHasSearched(false);
    onClose();
  };

  return (
    <Modal
      title={i18n.t(LOCALS.bind_authentication_result)}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Input.Search
        placeholder={String(
          i18n.t(LOCALS.bind_authentication_search_placeholder),
        )}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onSearch={handleSearch}
        enterButton
        allowClear
        className="mb-4"
      />

      <Spin spinning={searching}>
        {taskList.length > 0 ? (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {taskList.map((item) => {
              const displaySummary = getAuthenticationDisplaySummary({
                latestResult: item.result,
                detectionPoints: item.result?.detectionPoints,
              });
              const verdictText = getAuthenticationDisplayVerdictText(
                displaySummary,
                i18n.t.bind(i18n),
              );
              const isWarningVerdict = displaySummary.mode !== 'normal';
              const verdictStyle = isWarningVerdict
                ? { backgroundColor: '#FFF9C4', color: '#F57F17' }
                : getAuthenticationDetailVerdictStyle(
                    displaySummary.displayVerdictKind,
                    displaySummary.displayScore,
                  );

              return (
                <Card
                  key={item.id}
                  size="small"
                  bodyStyle={{ padding: '12px 16px' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">
                        {item.certificateNo || item.authenticationId}
                      </span>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>
                          {String(item.bagInfo?.brand ?? '')}
                          {' '}
                          {String(
                            item.bagInfo?.name ?? item.bagInfo?.names ?? '',
                          )}
                        </span>
                        {verdictText && (
                          <Tag
                            icon={
                              isWarningVerdict ? (
                                <WarningOutlined />
                              ) : (
                                <CheckCircleOutlined />
                              )
                            }
                            style={verdictStyle}
                            className="m-0"
                          >
                            {verdictText}
                          </Tag>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">
                        {formatTime(item.createdAt)}
                      </span>
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      loading={bindingId === item.authenticationBagId}
                      onClick={() => handleSelect(item)}
                    >
                      {i18n.t(LOCALS.bind_authentication_bind)}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          hasSearched &&
          !searching && (
            <Empty
              description={i18n.t(LOCALS.bind_authentication_no_result)}
            />
          )
        )}
      </Spin>
    </Modal>
  );
};

export default BindAuthenticationResultModal;
